/**
 * The case's hypotheses at a glance, as the Case summary opens with them (PRD-0075 R-E5): each
 * with its latest verdict and confidence and when it was assessed. The judgment shown is the
 * analysts' latest assessment; a hypothesis not assessed yet says so.
 */
import { Lightbulb } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Skeleton } from '@/components/ui/skeleton'
import type { HypothesisConfidence, HypothesisVerdict } from '@/types/analytic-hypothesis'
import { VerdictBadge } from './HypothesisCard'

const COPY = 'analyticHypotheses'

export interface HypothesisDigestItem {
  key: string
  title: string
  /** The latest assessment; both null before the first one. */
  verdict: HypothesisVerdict | null
  confidence: HypothesisConfidence | null
  /** When it was assessed, ready to show; null before the first assessment. */
  assessedAt: string | null
  /** The hypothesis page, when the reader can open it. */
  href?: string
}

export interface HypothesesDigestViewProps {
  /** Null while they are read. */
  items: readonly HypothesisDigestItem[] | null
}

function Row({ item }: { item: HypothesisDigestItem }) {
  const { t } = useTranslation()
  const latest =
    item.verdict && item.confidence ? { verdict: item.verdict, confidence: item.confidence } : null
  const title = item.href ? (
    <Link
      to={item.href}
      aria-label={t(`${COPY}.card.open`, { title: item.title })}
      className="min-w-0 flex-1 truncate font-medium text-foreground hover:underline"
    >
      {item.title}
    </Link>
  ) : (
    <span className="min-w-0 flex-1 truncate font-medium text-foreground">{item.title}</span>
  )
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
      <Lightbulb aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
      {title}
      <VerdictBadge point={latest} />
      {latest && item.assessedAt && (
        <span className="font-mono text-2xs text-muted-foreground">
          {t(`${COPY}.digest.assessedOn`, { date: item.assessedAt })}
        </span>
      )}
    </li>
  )
}

export function HypothesesDigestView({ items }: HypothesesDigestViewProps) {
  const { t } = useTranslation()
  return (
    <section className="flex flex-col gap-2" aria-label={t(`${COPY}.digest.title`)}>
      <h4 className="text-3xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        {t(`${COPY}.digest.title`)}
      </h4>
      {items === null ? (
        <Skeleton aria-hidden className="h-6 w-2/3" />
      ) : items.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t(`${COPY}.digest.empty`)}</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {items.map(item => (
            <Row key={item.key} item={item} />
          ))}
        </ul>
      )}
    </section>
  )
}
